'use client';

import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from 'react';
import { getFiatWalletWithAccountInfo } from '../../../common/gqlActions/getFiatWalletWithAccountInfo.action';
import { useHandleGqlErrorsWithSnackbar } from '../../../common/utils/errors/useHandleGqlErrorsWithSnackbar';

type FiatWalletData = Awaited<
  ReturnType<typeof getFiatWalletWithAccountInfo>
>['data'];

interface FiatWalletContextValue {
  data: FiatWalletData | null;
  isLoading: boolean;
  hasError: boolean;
}

const defaultValue: FiatWalletContextValue = {
  data: null,
  isLoading: false,
  hasError: false,
};

const FiatWalletContext = createContext<FiatWalletContextValue>(defaultValue);

interface FiatWalletProviderProps extends PropsWithChildren {
  enabled: boolean;
}

const FiatWalletProvider = ({ children, enabled }: FiatWalletProviderProps) => {
  const [data, setData] = useState<FiatWalletData | null>(null);

  const [isLoading, setIsLoading] = useState(enabled);

  const [hasError, setHasError] = useState(false);

  const { processErrors } = useHandleGqlErrorsWithSnackbar();

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    setIsLoading(true);

    getFiatWalletWithAccountInfo()
      .then((response) => {
        if (cancelled) return;

        if (response.errors) {
          setHasError(true);

          processErrors(response.errors);
        } else {
          setData(response.data);
        }

        setIsLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;

        setHasError(true);

        setIsLoading(false);

        processErrors(error);
      });

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return (
    <FiatWalletContext.Provider value={{ data, isLoading, hasError }}>
      {children}
    </FiatWalletContext.Provider>
  );
};

const useFiatWallet = () => useContext(FiatWalletContext);

export { FiatWalletProvider, useFiatWallet };
